import { useCallback } from "react";
import type React from "react";
import type { PaginationState } from "../types";

/**
 * Pagination event handlers
 */
export function usePaginationHandlers(
  currentPage: number,
  setCurrentPage: (page: number) => void,
  data:
    | {
        pageInfo?: {
          endCursor?: string | null;
        };
      }
    | undefined,
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
) {
  // Cursor navigation handlers
  const handlePageChange = useCallback(
    (page: number) => {
      if (page === currentPage) {
        return;
      }

      if (page > currentPage) {
        // Going to next page
        // Try cursor-based pagination first (for GraphQL APIs that support it)
        const endCursor = data?.pageInfo?.endCursor;
        if (endCursor) {
          // Set cursor AND increment page number
          // This ensures the query params change and trigger a new GraphQL query
          setPagination((prev) => {
            return { ...prev, cursor: endCursor, page: page };
          });
        } else {
          // Fall back to offset-based pagination (for REST APIs or when cursor is not available)
          // Just update the page number and let the query params handle the offset
          setPagination((prev) => ({ ...prev, cursor: null, page: page }));
        }
        setCurrentPage(page);
      } else {
        // Going to previous page - for now, reset to first page
        // In a full implementation, you'd need to track cursor history
        setPagination((prev) => ({ ...prev, cursor: null, page: 0 }));
        setCurrentPage(0);
      }
    },
    [currentPage, data?.pageInfo?.endCursor, setCurrentPage, setPagination]
  );

  // Page size change handler - resets to first page when page size changes
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPagination((prev) => ({ ...prev, pageSize: newPageSize, cursor: null, page: 0 }));
      setCurrentPage(0);
    },
    [setPagination, setCurrentPage]
  );

  return {
    handlePageChange,
    handlePageSizeChange,
  };
}
